import {
  addHours, isAfter, isBefore,
  roundToNearestMinutes,
} from 'date-fns';
import { TFunction } from 'i18next';
import { CAPTAIN_OPTION_1_INCLUDED_IN_PRICE, CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN, CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE } from 'src/common/constants';
import { timeStringToDate } from 'src/common/helpers';
import { BlockedTime } from 'src/common/providers/useBookingMotivators';
import { AuthUserApiResponse } from 'src/common/types/api/v4/auth/user';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { InstabookAvailability } from 'src/common/types/api/v4/instabook';
import { CaptainOptionValue } from 'src/common/types/CaptainOptions';
import { convertToTimeZone } from 'src/common/utils/dateHelpers';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import {
  getDurationHours, getMinMaxHoursForPreferredPriceTier, sortInstabookTripsByPrice, UnitPriceDetail,
} from 'src/listing/helper';
import { DepartureTime, TripCartFormState } from 'src/listing/types';
import {
  getDurationOptions, hasFullDay, mergeDurationInstabookOptions, TripCartDurationOption,
} from 'src/trip-cart-deprecated/utils/tripCartUtils';
import getInstabookDepartureTimes from 'src/listing/utils/getInstabookDepartureTimes';

/**
 * These are the possible options the form can be changed to given its current state (form values).
 * Initial values are also part of these options, merged together.
 *
 * In some fields, an array is used, this means there are several possibilities to change to. If
 * the array is length 2 or more, there are other values this field can be changed to. Otherwise
 * there are no other values to change to.
 *
 * In some fields, there is only one value. This means it is configured for only one result ever,
 * or else it is a field that is not part of the form, and is instead derived from form data.
 */
export type TripCartOptions = {
  // Same keys as TripCartFormState
  // All possible form durations
  formDuration: TripCartDurationOption[];
  // All captain options
  withCaptain: CaptainOptionValue[];
  // Can be true or false or both
  isMultiday: boolean[];
  // Maximum capacity. Minimum is 1, used directly on the form itself
  formGuests: number; // maximum capacity
}
// custom fields
& {
  // Descriptive text for what the selected or only captain option means
  captainText: CaptainText;
  // If captains are included, the price can have a separate captain line item
  hasCaptainFee: boolean;
  // Instabooks are confirmed prices, inquiries are estimates
  isCostConfirmed: boolean;
  calendar: {
    // all other dates will be disabled. If undefined, all dates are available
    enableSpecificDates: string[] | undefined;
  };
  // CTAs
  actions: Actions;
  errors?: {
    [field in keyof Pick<TripCartFormState, 'departureTime' | 'withCaptain'>]?: ErrorMessage;
  };
};

type CaptainText = {
  // Used for translation keys
  key: 'self-captain' | 'with-captain' | 'with-captain--separate-fee';
  hasDescription: boolean;
  hasTooltip: boolean;
};

type Actions = {
  instabookVisible: boolean;
  instabookEnabled: boolean;
  inquiryEnabled: boolean;
  isOwnBoat: boolean;
};

type ErrorMessage = string;

export function getMultiday({ values, unitPriceDetail, instabookDates }: {
  values: TripCartFormState ;
  unitPriceDetail: UnitPriceDetail;
  instabookDates: string[];
}): TripCartOptions['isMultiday'] {
  const isMultiday = [values.isMultiDay];
  if (!values.isMultiDay && unitPriceDetail?.multiday?.length) {
    const hasOneDay = hasFullDay(unitPriceDetail);
    if (hasOneDay
      && (unitPriceDetail.multiday[0].max_unit_quantity > 1
      || unitPriceDetail.multiday.length > 1)
    ) {
      isMultiday.push(true);
    } else if (!hasOneDay) {
      isMultiday.push(true);
    }
  } else if (values.isMultiDay
      && (
        unitPriceDetail?.hour?.length
        || hasFullDay(unitPriceDetail)
        || instabookDates.length)
  ) {
    isMultiday.push(false);
  }
  return isMultiday;
}

export function getCaptainInfo({
  listing, values, unboundedInstabook, featureFlags,
}: {
  values: TripCartFormState;
  listing: FullBoatSearchDetail;
  unboundedInstabook?: InstabookAvailability;
  featureFlags?: {
    summary?: boolean;
  };
}): {
    withCaptain: TripCartOptions['withCaptain'];
    captainText: TripCartOptions['captainText'];
    hasCaptainFee: TripCartOptions['hasCaptainFee'];
    error?: ErrorMessage;
  } {
  let hasCaptainFee = true;
  const captainText: Partial<CaptainText> = {
    hasDescription: true,
    hasTooltip: true,
  };

  let withCaptain = listing.captain_options;

  // if there is an instabook out of tier bounds, ensure we lock the options down
  if (unboundedInstabook) {
    const listingCaptained = listing.captain_options.find((option) => (
      option !== CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE
    )) ?? CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN; // Should not occur, safety backup for data issues

    withCaptain = unboundedInstabook.captained
      ? [listingCaptained]
      : [CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE];
  }

  // Stop calculating, invalid option
  if (!withCaptain.includes(values.withCaptain)) {
    return {
      // include valid options it should be changed to
      withCaptain,
      // Values avoid component crashes
      captainText: {} as CaptainText,
      hasCaptainFee,
      // Error is discarded, only used in logic. No need to translate
      error: 'invalid',
    };
  }

  if (values.withCaptain === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE) {
    captainText.key = 'with-captain';
    hasCaptainFee = false;
  } else if (values.withCaptain === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN) {
    const captainCost = listing.captain_hourly_cost;
    if (!captainCost
      || values.isMultiDay
      || getDurationHours(values.formDuration) === FULL_DAY_HOURS
    ) {
      if (featureFlags?.summary) {
        captainText.key = 'with-captain';
      } else {
        captainText.key = 'with-captain--separate-fee';
      }
      captainText.hasTooltip = false;
    } else {
      captainText.key = 'with-captain';
      captainText.hasDescription = false;
      captainText.hasTooltip = false;
    }
  } else {
    // CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE
    captainText.key = 'self-captain';
    hasCaptainFee = false;
  }

  return {
    hasCaptainFee,
    captainText: captainText as CaptainText,
    withCaptain,
  };
}

function getCalendar({
  values: { isMultiDay },
  unitPriceDetail,
  instabookDates,
}: {
  values: TripCartFormState;
  unitPriceDetail: UnitPriceDetail;
  instabookDates: string[];
}): TripCartOptions['calendar'] {
  const hasOneDay = hasFullDay(unitPriceDetail);

  // multiday only, but selecting non-multiday i.e. instabook only
  const showOnlyInstabookDates = !isMultiDay && !hasOneDay && !unitPriceDetail.hour?.length;
  if (showOnlyInstabookDates) {
    return {
      // all other dates will be disabled
      enableSpecificDates: instabookDates,
    };
  }
  return {
    enableSpecificDates: undefined,
  };
}

export function captainValueEqualsInstabook(
  withCaptainValue: CaptainOptionValue,
  instabookAvailability?: InstabookAvailability,
): boolean {
  const isInstabookCaptained = instabookAvailability?.captained;
  const isValueCaptained = withCaptainValue !== CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE;
  return isInstabookCaptained === isValueCaptained;
}

export function getActions({
  user,
  values,
  listing,
  instabookDurations,
  isDateUnavailable,
  selectedInstabookAvailability,
  previewMode,
  hasErrors,
}: {
  user: Partial<AuthUserApiResponse> | null;
  values: TripCartFormState;
  listing: FullBoatSearchDetail;
  instabookDurations: number[];
  isDateUnavailable: (dates: Date[], isMultiDay: boolean) => boolean;
  selectedInstabookAvailability?: InstabookAvailability;
  previewMode: boolean;
  hasErrors?: boolean;
}): Actions {
  const isUnavailable = isDateUnavailable(values.formSelectedDates, values.isMultiDay);

  const isOwnBoat = user?.id === listing.user_details.id;

  let instabookVisible = !values.isMultiDay
    && !!(listing.has_available_instabook && listing.instabook_enabled)
    && instabookDurations.includes(getDurationHours(values.formDuration));

  // Only available when matching captain
  if (instabookVisible && !captainValueEqualsInstabook(
    values.withCaptain,
    selectedInstabookAvailability,
  )) {
    instabookVisible = false;
  }

  return {
    instabookVisible,
    /**  TODO: add `!isOwnBoat` after @feature `conversion-mission` is released */
    instabookEnabled: !hasErrors && !isUnavailable && instabookVisible && !previewMode,
    /**  TODO: add `!isOwnBoat` after @feature `conversion-mission` is released */
    inquiryEnabled: !hasErrors && !isUnavailable && !previewMode,
    isOwnBoat,
  };
}

function getSortedInstabooksForDuration(
  durationHours: number,
  instabookAvailabilities: InstabookAvailability[],
) {
  const availabilities = instabookAvailabilities.filter(
    (trip) => Number(trip.trip_length.split(':')[0]) === durationHours,
  ).slice().sort(sortInstabookTripsByPrice);
  return availabilities;
}

export function getMatchingInstabooks(
  values: TripCartFormState,
  instabookAvailabilities: InstabookAvailability[],
): InstabookAvailability[] {
  const durationHours = getDurationHours(values.formDuration);
  const availabilities = getSortedInstabooksForDuration(durationHours, instabookAvailabilities);

  // prefer form selection
  const captainPreference = values.withCaptain !== CAPTAIN_OPTION_4_NOT_INCLUDED_IN_PRICE;
  return availabilities.filter((availability) => (
    availability.captained === captainPreference
  ));
}

export function getMatchingInstabookAvailability(
  values: TripCartFormState,
  instabookAvailabilities: InstabookAvailability[],
): InstabookAvailability | undefined {
  const instabooks = getMatchingInstabooks(values, instabookAvailabilities);
  return instabooks?.[0];
}

/**
 * Some instabook configs have multiple matching instabook availabilities
 * Returning only one instabook match would limit selectable departureTimes
 * This matches multiple and returns all of their times
 */
export function setupDepartureTimes(
  values: TripCartFormState,
  instabookAvailabilities: InstabookAvailability[],
  locale: Locale,
): {
    /** instabook departureTimes to show in the dropdown */
    tripTimes: DepartureTime[];
    /** filters by > duration >  withCaptain > cheapest renter_booking_amount */
    defaultInstabook: InstabookAvailability | undefined;
  } {
  const instabooks = getMatchingInstabooks(values, instabookAvailabilities);

  const tripTimes = instabooks.flatMap(
    (instabook) => getInstabookDepartureTimes(instabook, locale),
  );
  /** relies on getMatchingInstabooks sort by price */
  const uniqueTimes = new Set<string>();
  const dedupedTimes = tripTimes.filter((time) => {
    const unique = !uniqueTimes.has(time.formattedTime24);
    uniqueTimes.add(time.formattedTime24);
    return unique;
  });

  return {
    tripTimes: dedupedTimes.sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime()),
    defaultInstabook: instabooks[0],
  };
}

/**
 * Returns the closest instabook match when using an instabook-only duration, but no instabooks
 * are available for the current form state (captain option mismatch).
 * Normal price tiers are out of bounds.
 * @returns `null` if there already is a selectedInstabook or in duration bounds
 *          OR best matched instabook availability
 */
function getUnboundedInstabookAvailabilityForDuration({
  instabookAvailabilities,
  isInstabookDurationSelected,
  values,
  unitPriceDetail,
}: {
  instabookAvailabilities: InstabookAvailability[];
  isInstabookDurationSelected: boolean;
  values: TripCartFormState;
  unitPriceDetail: UnitPriceDetail;
}): InstabookAvailability | undefined {
  if (!isInstabookDurationSelected) {
    return undefined;
  }

  const durationHours = getDurationHours(values.formDuration);
  if (values.isMultiDay || durationHours >= FULL_DAY_HOURS) {
    return undefined;
  }

  const clamped = unitPriceDetail.hour?.length
    ? getMinMaxHoursForPreferredPriceTier(unitPriceDetail.hour)!
    // multiday only, so always out of bounds
    : null;

  // outside of bounds
  if (!clamped || durationHours < clamped.min || durationHours > clamped.max) {
    // get all availabilities for duration
    const availabilities = getSortedInstabooksForDuration(durationHours, instabookAvailabilities);

    if (availabilities.length) {
      return availabilities[0];
    }
  }

  return undefined;
}

export function roundedDepartureTime(departureTime: string, date?: Date): Date {
  const value = timeStringToDate(departureTime.toString(), date);
  // Only allows 15 minute increments
  return roundToNearestMinutes(value, { nearestTo: 15 });
}

export function validateDepartureTime({
  value, durationHours, blockedTimes, date, listingTimeZone, t,
}: {
  value: string;
  durationHours: number;
  blockedTimes: BlockedTime[];
  date: Date;
  listingTimeZone: string | undefined;
  t: TFunction;
}): { error: ErrorMessage } | undefined {
  if (!listingTimeZone) return undefined;

  const MIN_HOURS_BEFORE_INQUIRY = 1;
  // TODO: check min hour requirement
  const currentDateTime = convertToTimeZone(
    addHours(new Date(), MIN_HOURS_BEFORE_INQUIRY),
    listingTimeZone,
  );

  // Don't round here, as can seem confusing for free text entry
  const selectedDateTime = timeStringToDate(value, date);
  const isDateInFuture = isBefore(currentDateTime, selectedDateTime);

  if (!isDateInFuture) {
    return { error: t('trip-cart--departure-time--error-past') };
  }

  const selectedDateTimeDuration = addHours(selectedDateTime, durationHours);
  const blocked = blockedTimes.find((range) => {
    const start = timeStringToDate(range.start_time, date);
    const end = timeStringToDate(range.end_time, date);

    return isAfter(selectedDateTimeDuration, start) && isBefore(selectedDateTime, end);
  });

  if (blocked) {
    return {
      error:
      t(
        'trip-cart--departure-time--error-blocked',
        { startTime: blocked.start_time, endTime: blocked.end_time },
      ),
    };
  }
  return undefined;
}

function getDepartureTime({
  values, departureTimes, blockedTimes, listingTimeZone, hasSubmitted, t,
}: {
  values: TripCartFormState;
  // Undefined for inquiry
  departureTimes: DepartureTime[] | undefined;
  blockedTimes: BlockedTime[];
  listingTimeZone: string | undefined;
  hasSubmitted?: boolean;
  t: TFunction;
}): {
    error?: ErrorMessage;
  } {
  // TODO: id
  const value = values.departureTime ? values.departureTime : undefined;

  // free-entry
  if (value && !departureTimes) {
    const durationHours = getDurationHours(values.formDuration);
    const result = validateDepartureTime({
      value,
      durationHours,
      date: values.formSelectedDates[0],
      blockedTimes,
      listingTimeZone,
      t,
    });

    // Keep value, even if error since this is free-entry
    return { error: result?.error };
  }
  // instabook: explicit entry
  if (value && departureTimes?.find((time) => time.formattedTime24 === value)) {
    return { error: undefined };
  }

  if (value && departureTimes?.length) {
    return { error: t('trip-cart--form-error--departure-time') };
  }

  if (!value && hasSubmitted) {
    return { error: t('trip-cart--form-error--departure-time') };
  }
  // No concerns, all errors already caught
  return { error: undefined };
}

function getTripCartOptions({
  user,
  values,
  unitPriceDetail,
  instabookDates,
  instabookDurations,
  listing,
  selectedInstabookAvailability,
  instabookAvailabilities,
  isDateUnavailable,
  departureTimes,
  blockedTimes,
  previewMode,
  hasSubmitted,
  featureFlags,
  t,
}: {
  user: Partial<AuthUserApiResponse> | null;
  values: TripCartFormState;
  unitPriceDetail: UnitPriceDetail;
  instabookDates: string[];
  instabookDurations: number[];
  listing: FullBoatSearchDetail;
  instabookAvailabilities: InstabookAvailability[];
  selectedInstabookAvailability?: InstabookAvailability;
  isDateUnavailable: (dates: Date[], isMultiDay: boolean) => boolean;
  departureTimes: DepartureTime[] | undefined;
  blockedTimes: BlockedTime[];
  previewMode: boolean;
  hasSubmitted: boolean;
  t: TFunction;
  featureFlags?: {
    summary?: boolean;
  };
}): TripCartOptions {
  // Durations
  let formDuration = getDurationOptions(unitPriceDetail, values.isMultiDay);
  if (!values.isMultiDay) {
    formDuration = mergeDurationInstabookOptions(formDuration, instabookDurations);
  }

  // Multiday
  const isMultiday = getMultiday({ values, unitPriceDetail, instabookDates });

  const isInstabookDurationSelected = instabookDurations.includes(
    getDurationHours(values.formDuration),
  );
  const unboundedInstabook = getUnboundedInstabookAvailabilityForDuration({
    instabookAvailabilities,
    isInstabookDurationSelected,
    values,
    unitPriceDetail,
  });

  // Captain
  const {
    captainText, hasCaptainFee, withCaptain, error: withCaptainError,
  } = getCaptainInfo({
    listing,
    values,
    unboundedInstabook,
    featureFlags,
  });

  const calendar = getCalendar({
    values,
    unitPriceDetail,
    instabookDates,
  });

  const { error: departureTimeError } = getDepartureTime({
    values,
    departureTimes,
    blockedTimes,
    listingTimeZone: listing.timezone,
    hasSubmitted,
    t,
  });

  const errors: TripCartOptions['errors'] = {
    withCaptain: withCaptainError,
    departureTime: departureTimeError,
  };

  // Remove any keys that have no value
  Object.entries(errors).forEach(([key, value]) => {
    if (!value) delete errors[key];
  });

  // Actions
  const actions = getActions({
    user,
    listing,
    values,
    instabookDurations,
    isDateUnavailable,
    previewMode,
    selectedInstabookAvailability,
    hasErrors: Object.keys(errors).length > 0,
  });

  const isCostConfirmed = actions.instabookEnabled;

  return {
    formDuration,
    withCaptain,
    isMultiday,
    formGuests: listing.capacity,
    // custom
    isCostConfirmed,
    captainText,
    hasCaptainFee,
    calendar,
    actions,
    errors: Object.keys(errors).length ? errors : undefined,
  };
}

export default getTripCartOptions;
